import { computed } from 'vue'

import { websiteSlugFromLegacyShareFileName } from '@/utils/publishing'
import LayoutAuth from '@/views/layouts/LayoutAuth.vue'
import LayoutDashboard from '@/views/layouts/LayoutDashboard.vue'
import LayoutShared from '@/views/layouts/LayoutShared.vue'
import LayoutPassThrough from '@/views/layouts/PassThrough.vue'

import { AuthRoutes } from './routeGroups/auth'

import type { RouteRecordRaw } from 'vue-router'

const routes = computed<RouteRecordRaw[]>(() => [
  /**
   * Legacy per-file share links (`/share/html/{chatId}/{messageId}/{fileId}.html`),
   * from before published HTML became a Website (EVGPT-1592). Those links are out in
   * the wild, so they redirect to the current viewer instead of dead-ending: a
   * migrated asset keeps its file id as its website slug, so the link's file name
   * minus `.html` is the `/w/{slugOrId}` segment. Query and hash are carried over by
   * vue-router; `allowWithoutEverGptAccess` comes from the target route.
   */
  {
    path: '/share/html/:chatId/:messageId/:fileName',
    redirect: (to) => {
      const slugOrId = websiteSlugFromLegacyShareFileName(to.params.fileName)
      if (!slugOrId) return { name: 'NewChat' }
      return { name: 'SharePublishedWebsite', params: { slugOrId } }
    },
  },
  {
    path: '/w',
    component: LayoutShared,
    children: [
      {
        path: ':slugOrId',
        name: 'SharePublishedWebsite',
        component: () => import('@/views/pages/Share/PublishedWebsite.vue'),
        meta: {
          allowWithoutEverGptAccess: true,
        },
      },
    ],
  },
  /**
   * Standalone, chrome-less public GPT page. Needs no account at all
   * (`allowAnonymous`): `/public/gpts/...` is served without a JWT, so a visitor with
   * no Foundever session chats here instead of being bounced to a login page — which
   * is the entire point of a public share link, and the only thing that works inside
   * a frame, where Keycloak's login page refuses to render.
   *
   * `allowWithoutEverGptAccess` is therefore gone: it only ever meant "a session, but
   * no EverGPT role", and `allowAnonymous` subsumes it in both guards. A visitor who
   * does hold a session still has their bearer attached, which is what lights up
   * "Open this GPT" — the only way into the full app from here (no auto-redirect).
   */
  {
    path: '/public/gpt',
    component: LayoutShared,
    children: [
      {
        path: ':shareToken',
        name: 'PublicGpt',
        component: () => import('@/views/pages/PublicGpt/Index.vue'),
        meta: {
          allowAnonymous: true,
        },
      },
    ],
  },
  {
    path: '/not-authorised',
    name: 'NotAuthorised',
    component: () => import('@/views/pages/NotAuthorised/Index.vue'),
    meta: {
      showFooter: true,
      showModalSelector: true,
    },
  },
  {
    name: 'LayoutDashboard',
    path: '/',
    component: LayoutDashboard,
    children: [
      {
        path: '',
        name: 'NewChat',
        component: () => import('@/views/pages/Home/Index.vue'),
        meta: {
          showFooter: true,
          showModalSelector: true,
        },
      },
      {
        path: 'chat',
        name: 'ChatLoading',
        component: () => import('@/views/pages/ChatLoading/Index.vue'),
        meta: {
          showFooter: true,
          showModalSelector: true,
        },
      },
      {
        path: 'chat/:id',
        name: 'Chat',
        component: () => import('@/views/pages/Chat/Index.vue'),
        meta: {
          showFooter: true,
          showModalSelector: true,
        },
      },
      // GPTs
      {
        path: 'gpts',
        name: 'GptList',
        component: () => import('@/views/pages/Gpt/List/Index.vue'),
        meta: {
          breadcrumb: 'GPTs',
        },
      },
      {
        path: 'library',
        name: 'Library',
        component: () => import('@/views/pages/Library/Index.vue'),
        meta: {
          breadcrumb: 'Library',
        },
      },
      {
        // The MCP OAuth callback redirects here with `?connected=<slug>`.
        path: 'connectors',
        name: 'Connectors',
        component: () => import('@/views/pages/Connectors/Index.vue'),
        meta: {
          breadcrumb: 'Connectors',
        },
      },
      {
        path: 'gpt/:uuid',
        name: 'GptUpdate',
        component: () => import('@/views/pages/Gpt/Upsert/Index.vue'),
        meta: {
          noContainerPadding: true,
          breadcrumb: 'Update GPT',
        },
      },
      {
        path: 'gpt',
        name: 'GptCreate',
        component: () => import('@/views/pages/Gpt/Upsert/Index.vue'),
        meta: {
          noContainerPadding: true,
          breadcrumb: 'Create GPT',
        },
      },
      {
        path: 'admin',
        name: 'Admin',
        component: LayoutPassThrough,
        meta: {
          breadcrumb: 'Admin',
          isAdmin: true,
        },
        children: [
          {
            path: '',
            name: 'AdminIndex',
            component: () => import('@/views/pages/Admin/Index.vue'),
          },
          // #region Admin GPT pages
          {
            path: 'gpt/:uuid',
            name: 'AdminGptUpdate',
            component: () => import('@/views/pages/Gpt/Upsert/AdminIndex.vue'),
            meta: {
              noContainerPadding: true,
              breadcrumb: 'Update GPT',
            },
          },
          // #endregion
        ],
      },
    ],
  },
  // Auth pages
  {
    path: '/',
    component: LayoutAuth,
    children: AuthRoutes,
    meta: {
      isPublic: true,
    },
    redirect: {
      name: 'Login',
    },
  },
])

export default routes
